import { type ColumnDef } from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { MapPin } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { appLink } from '@app/config/appLink'
import { COLUMN_LABELS } from '../documents.constants'
import { DocumentColumnKey, DocumentsSortField, EvidenceDocument } from '../documents.types'
import { formatDate, formatFileSize, getDocumentType } from '../documents.utils'
import { DocumentRowActions } from './document-row-actions'

// Preserved on the Title link so opening a document keeps the list's filters/sort/page
// intact. A real <Link>, not onClick, so right-click -> open in new tab works.
export const createTitleColumn = (search: string): ColumnDef<EvidenceDocument> => ({
  id: 'title',
  header: 'Title',
  cell: ({ row }) => (
    <Link
      to={{ pathname: `${appLink.documents.path}/${row.original.id}`, search }}
      className="text-foreground hover:underline text-left"
    >
      {row.original.title}
    </Link>
  ),
})

export const createActionsColumn = (
  search: string,
  onEdit: (document: EvidenceDocument) => void,
  onDelete: (document: EvidenceDocument) => void,
): ColumnDef<EvidenceDocument> => ({
  id: 'actions',
  header: '',
  cell: ({ row }) => (
    <DocumentRowActions
      document={row.original}
      search={search}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  ),
})

export const OPTIONAL_COLUMN_DEFS: Record<DocumentColumnKey, ColumnDef<EvidenceDocument>> = {
  category: {
    id: 'category',
    header: COLUMN_LABELS.category,
    cell: ({ row }) => row.original.category,
  },
  documentType: {
    id: 'documentType',
    header: COLUMN_LABELS.documentType,
    cell: ({ row }) => getDocumentType(row.original.file.originalFilename),
  },
  dateUploaded: {
    id: 'dateUploaded',
    header: COLUMN_LABELS.dateUploaded,
    cell: ({ row }) => formatDate(row.original.uploadedAt),
  },
  owner: {
    id: 'owner',
    header: COLUMN_LABELS.owner,
    cell: ({ row }) => {
      const owner = row.original.ownerEmail ?? 'Deleted user'
      return (
        <span className="block max-w-[220px] truncate" title={owner}>
          {owner}
        </span>
      )
    },
  },
  tags: {
    id: 'tags',
    header: COLUMN_LABELS.tags,
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1 max-w-[220px]">
        {row.original.tags.map((tag) => (
          <Badge key={tag} variant="secondary" size="sm">
            {tag}
          </Badge>
        ))}
      </div>
    ),
  },
  source: {
    id: 'source',
    header: COLUMN_LABELS.source,
    cell: ({ row }) => row.original.source ?? '—',
  },
  datePublished: {
    id: 'datePublished',
    header: COLUMN_LABELS.datePublished,
    cell: ({ row }) => formatDate(row.original.publishedAt),
  },
  organisation: {
    id: 'organisation',
    header: COLUMN_LABELS.organisation,
    cell: ({ row }) => row.original.organisation ?? '—',
  },
  fileSize: {
    id: 'fileSize',
    header: COLUMN_LABELS.fileSize,
    cell: ({ row }) => formatFileSize(row.original.file.sizeBytes),
  },
  georeferenced: {
    id: 'georeferenced',
    header: COLUMN_LABELS.georeferenced,
    cell: ({ row }) =>
      row.original.georeference.type !== 'NONE' ? <MapPin className="h-4 w-4 text-primary" /> : '—',
  },
  permissions: {
    id: 'permissions',
    header: COLUMN_LABELS.permissions,
    cell: ({ row }) => (
      <Badge variant="outline" size="sm" className="capitalize">
        {row.original.visibility}
      </Badge>
    ),
  },
}

export const COLUMN_TO_SORT_FIELD: Partial<Record<string, DocumentsSortField>> = {
  title: 'title',
  category: 'category',
  dateUploaded: 'created_at',
  datePublished: 'published_at',
  fileSize: 'size_bytes',
}
